import type { SupportTicketThreadContext } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import GitBranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

/**
 * Visual break between two channel threads on the same support ticket.
 *
 * In the v2 topic-span model, one ticket can accumulate multiple threads
 * (e.g., a customer who started on email and escalated to SMS). This divider
 * separates consecutive threads before the next `ThreadHeader` names the
 * channel, subject, and most-recent-message time.
 *
 * Most multi-thread tickets are just normal v2 topic shape. When the backend
 * marks a lane as active merge context, this divider labels that boundary so
 * agents know replies use the original merged thread.
 */
export const ThreadBoundaryDivider = memo(function ThreadBoundaryDivider({
  ticketContext,
}: {
  ticketContext?: SupportTicketThreadContext;
}) {
  if (ticketContext?._tag !== "mergedSource") {
    return (
      <Flex
        borderColor="secondary"
        borderStyle="solid"
        borderTopWidth="1px"
        my="md"
        w="full"
      />
    );
  }

  const ticketLabel =
    ticketContext.sourceTicketNumber !== null
      ? `Merged from ticket #${ticketContext.sourceTicketNumber}`
      : "Merged from another ticket";

  return (
    <Flex align="center" gap="md" my="md" w="full">
      <Flex
        borderColor="secondary"
        borderStyle="solid"
        borderTopWidth="1px"
        grow={1}
      />
      <Flex
        align="center"
        bgColor="primary"
        borderColor="secondary"
        borderStyle="solid"
        borderWidth="1px"
        gap="xs"
        px="md"
        py="xs"
        radius="sm"
        wrap="wrap"
      >
        <RedoIcon color="tertiary" Icon={GitBranchIcon} size={14} />
        <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
          {ticketLabel}
        </Text>
        <Text fontSize="xs" textColor="tertiary">
          Replies here use the original thread
        </Text>
      </Flex>
      <Flex
        borderColor="secondary"
        borderStyle="solid"
        borderTopWidth="1px"
        grow={1}
      />
    </Flex>
  );
});
